import type {
    VideoTranscodingResponseDTO,
    SubFileStatusDTO,
} from '@capture/client-api/src/orval'
import { getCurrentLangDefinition, getDeviceProps } from '~/config/constants'
import type { RequiredCommonQueryParamsWithAuth } from '~/@types/backend-types'
import { HostUrl, getJSON } from '../toolbox'

export type VideoQuality = 'v-low' | 'v-high'

type VideoSubOption = VideoQuality | 'v-check'

type CheckVideoResponse = {
    result: SubFileStatusDTO
}

export class VideoService {
    private hostUrl: HostUrl

    constructor(hostname: string, authToken: string) {
        this.hostUrl = new HostUrl(hostname, {
            key: import.meta.env.VITE_API_KEY,
            auth: authToken,
            client_v: import.meta.env.VITE_VERSION,
            device_id: getDeviceProps().device_id,
            app_lang: getCurrentLangDefinition().appLang,
        } satisfies RequiredCommonQueryParamsWithAuth)
    }

    private getFileUrl(
        jobID: JobID,
        videoID: VideoID,
        sub: VideoSubOption,
    ): string {
        return this.hostUrl.getPath(
            `/st/4/jobs/${jobID}/files_by_id/${videoID}`,
            { sub },
        )
    }

    public checkVideo(
        jobID: JobID,
        videoID: VideoID,
    ): Promise<CheckVideoResponse> {
        return getJSON<CheckVideoResponse>(
            this.getFileUrl(jobID, videoID, 'v-check'),
        )
    }

    public getVideoUrl(
        jobID: JobID,
        videoID: VideoID,
        quality: VideoQuality,
    ): string {
        return this.getFileUrl(jobID, videoID, quality)
    }

    public async getVideoStream(jobID: JobID, fileID: FileID): Promise<string> {
        const response = await getJSON<VideoTranscodingResponseDTO>(
            this.hostUrl.getPath(`/st/4/jobs/${jobID}/video_url/${fileID}`),
        )
        return response.url
    }
}
